import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import './FeedSidebarArticles.css';

interface Article {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image: string | null;
  author: { id: number; first_name: string; last_name: string };
  published_at: string | null;
}

interface FeedSidebarArticlesProps {
  onViewArticle?: (slug: string) => void;
  onViewAll?: () => void;
}

const FeedSidebarArticles: React.FC<FeedSidebarArticlesProps> = ({ onViewArticle, onViewAll }) => {
  const { t } = useLanguage();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/articles?per_page=5`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setArticles(data.articles || []);
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchArticles();
  }, []);

  const formatDate = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="feed-sidebar-articles">
        <div className="sidebar-section-title">{t('feed.latestArticles')}</div>
        <div className="sidebar-articles-skeleton">
          {[1, 2, 3].map(i => (
            <div key={i} className="sidebar-article-skeleton-item">
              <div className="skeleton-line long" />
              <div className="skeleton-line short" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="feed-sidebar-articles">
      <div className="sidebar-section-title">{t('feed.latestArticles')}</div>

      {articles.length === 0 ? (
        <div className="sidebar-articles-empty">{t('feed.noArticles')}</div>
      ) : (
        <div className="sidebar-articles-list">
          {articles.map(article => (
            <div
              key={article.id}
              className="sidebar-article-item"
              onClick={() => onViewArticle?.(article.slug)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && onViewArticle?.(article.slug)}
            >
              <div className="sidebar-article-title">{article.title}</div>
              <div className="sidebar-article-meta">
                {formatDate(article.published_at)}
                {article.author && (
                  <> · {article.author.first_name} {article.author.last_name}</>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {articles.length > 0 && onViewAll && (
        <button className="sidebar-articles-view-all" onClick={onViewAll}>
          {t('feed.viewAll')}
        </button>
      )}
    </div>
  );
};

export default FeedSidebarArticles;
